import { Component, OnInit } from '@angular/core';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { ApplicationSettings, ApplicationService, IConfigFilter, ApplicationConfiguration } from '@services/application.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { firstValueFrom } from 'rxjs';

/** AppConfigComponent */
@Component({
	selector: 'app-app-config',
	templateUrl: './app-config.component.html',
	styleUrl: './app-config.component.scss'
})
export class AppConfigComponent implements OnInit {

	/** Application Settings */
	private appSettings: ApplicationSettings;

	/** Application Configuration */
	public appConfig: ApplicationConfiguration;

	/** Environment Variables */
	public env: any

	/** saving */
	saving: boolean = false;

	/** startDateInvalid */
	public startDateInvalid: boolean = false;
	/** endDateInvalid */
	public endDateInvalid: boolean = false;

	/**
	 * constructor
	 * @param applicationService 
	 * @param siteSubscriptions 
	 * @param globalFunctions 
	 */
	constructor(
		private applicationService: ApplicationService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions
	) {

		this.env = environment;
	}

	/** ngOnInit */
	async ngOnInit() {

		try {
			this.appSettings = await this.getApplicationSettings();
			this.appConfig = this.appSettings.Config;

			this.validateMaintenanceDates()
		}
		catch (error) {
			console.log(error);
		}
	}


	/**
	 * Return the default application configuration.
	 */
	private async getApplicationSettings(): Promise<ApplicationSettings> {
		try {
			const cfgFilter = <IConfigFilter>{
				application: this.env.appName,
				environment: this.env.production ? 'Production' : "Development"
			}
			const result = await firstValueFrom(
				this.applicationService.getSettings$(cfgFilter)
			);
			var appSettings = new ApplicationSettings(JSON.parse(result[0].Data));
			return appSettings;
		}
		catch (error: any) {
			if (error.status == 404) {
				console.log(error);
			} else {
				console.log(error);
				this.globalFunctions.HandleError(error, "Error: this.applicationService.getSettings$", false);
			}
		}
		finally { }

		return <ApplicationSettings>{}
	}


	/**
	 * Convert an epoch value into the format required
	 * by the datetime-local input.
	 * @param epoch 
	 * @returns 
	 */
	getDateTimeLocal(epoch: number | null = Date.now()): string {

		if (epoch === null) {
			return '';
		}

		const date = new Date(epoch);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');

		return `${year}-${month}-${day}T${hours}:${minutes}`;

	}

	// /**
	//  * Set the maintenance start time from the datetime-local input.
	//  * @param event 
	//  * @returns 
	//  */
	// setStartDate(event: Event) {

	// 	const input = event.target as HTMLInputElement;

	// 	if (!input.value) {
	// 		this.startDateInvalid = false;
	// 		this.appSettings.Config.maintenance.start = null;
	// 		return;
	// 	}

	// 	const date = new Date(input.value);
	// 	const epoch = date.getTime();

	// 	this.startDateInvalid = epoch < Date.now();

	// 	this.appSettings.Config.maintenance.start = epoch;

	// 	// if (!this.startDateInvalid) {
	// 	// 	this.appSettings.Config.maintenance.start = epoch;
	// 	// }
	// }

	// /**
	//  * Set the maintenance end time from the datetime-local input.
	//  * @param event 
	//  * @returns 
	//  */
	// setEndDate(event: Event): void {

	// 	const input = event.target as HTMLInputElement;

	// 	if (!input.value) {

	// 		this.appSettings.Config.maintenance.end = null;
	// 		this.endDateInvalid = false;

	// 		return;
	// 	}

	// 	const epoch = new Date(input.value).getTime();

	// 	this.endDateInvalid =
	// 		this.appSettings.Config.maintenance.start != null &&
	// 		epoch <= this.appSettings.Config.maintenance.start;

	// 	if (!this.endDateInvalid) {
	// 		this.appSettings.Config.maintenance.end = epoch;
	// 	}

	// }


	/**
 * Set the maintenance start time from the Syncfusion datetime picker.
 * @param input Syncfusion DateTimePicker change event.
 */
	setStartDateSF(input: any): void {

		if (!input.value) {
			this.appConfig.maintenance.start = null;
			this.validateMaintenanceDates();
			return;
		}

		this.appConfig.maintenance.start = new Date(input.value).getTime();

		this.validateMaintenanceDates();
	}

	/**
	 * Set the maintenance end time from the Syncfusion datetime picker.
	 * @param input Syncfusion DateTimePicker change event.
	 */
	setEndDateSF(input: any): void {

		if (!input.value) {
			this.appConfig.maintenance.end = null;
			this.validateMaintenanceDates();
			return;
		}

		this.appConfig.maintenance.end = new Date(input.value).getTime();

		this.validateMaintenanceDates();
	}

	/**
	 * Validate the maintenance start and end dates.
	 */
	validateMaintenanceDates(): void {

		const start = this.appConfig.maintenance.start;
		const end = this.appConfig.maintenance.end;
		const now = Date.now();

		// Start must not be in the past.
		this.startDateInvalid =
			start != null &&
			start < now;

		// End must be after Start.
		this.endDateInvalid =
			start != null &&
			end != null &&
			end <= start;
	}

	/**
	 * Save the application configuration.
	 */
	async save() {

		try {

			var SaveSettings = this.appConfig;
			/** Update the Config settings with new values */
			this.appSettings.Config = this.appConfig;

			/** Check if er have mainetnance enables and due now */
			if (this.appSettings.Config.maintenance.enabled &&
				this.appSettings.Config.maintenance.start != null &&
				this.appSettings.Config.maintenance.end != null
			) {
				if (this.startDateInvalid || this.endDateInvalid) {
					const start = new Date(this.appSettings.Config.maintenance.start).toLocaleString('en-GB', {
						dateStyle: 'short',
						timeStyle: 'short'
					});

					const end = new Date(this.appSettings.Config.maintenance.end).toLocaleString('en-GB', {
						dateStyle: 'short',
						timeStyle: 'short'
					});

					const OKResult = await firstValueFrom(
						this.globalFunctions.UserConfirm$(
							`Are you sure?\nCheck Maintenance dates\n\tStart:${start}\n\tEnd:${end}`,
							"Check Maintenance Dates"
						)
					);

					if (!OKResult) {
						return;
					}
				}
			}

			this.saving = true;
			const result = await firstValueFrom(
				this.applicationService.patchSettings$(this.appSettings.ApplicationConfigurationUID, this.appSettings)
			);

			this.appSettings = <ApplicationSettings>JSON.parse(result[0].Data);
			this.siteSubscriptions.setApplicationSettings(this.appSettings, true);

		}
		catch (error) {
			console.log(error);
		}
		finally {
			this.saving = false;
		}
	}

	/**
	 * Cancel changes and restore the original configuration.
	 */
	async cancel() {
		await this.ngOnInit()
	}

}
